import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import ShareIcon from '@mui/icons-material/Share';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    OutlinedInput,
    Select,
    SvgIconTypeMap,
    TextField,
    ThemeProvider,
    Typography,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';

import { CustomerUserDetails, EventDetails, EventTagActionEnum } from '../../../backendsdk';
import {
    TrackedButton as Button,
    TrackedDialog as Dialog,
    TrackedIconButton as IconButton,
    TrackedMenuItem as MenuItem,
} from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import { ExtendedFleetDriver } from '../../../hooks/drivers';
import useProfile from '../../../hooks/profile';
import { isEventVideo } from '../../../utils/File';
import { eventsPageDefs, flattenRoles } from '../../../utils/Pages';
import { SECONDS_IN_DAY, SECONDS_IN_HOUR, secondsToDurationString } from '../../../utils/TimeFormatter';
import palette from '../../ColorPalette';
import { theme } from '../Accident/MuiFormControls';
import { getUserAvatar } from '../Coaching/SessionDetails';
import { PHONE_NUMBER_MASK_MAP } from '../UserSettings/UserSettings';
import { OE_ROLES } from '../Users/UsersComponent';

enum Method {
    Link,
    WhatsApp,
    SendToUsers,
    SendToPhoneNumber,
}

const METHOD_TO_ICON: Record<
    Method,
    OverridableComponent<SvgIconTypeMap> & {
        muiName: string;
    }
> = {
    [Method.Link]: LinkIcon,
    [Method.WhatsApp]: WhatsAppIcon,
    [Method.SendToUsers]: PersonIcon,
    [Method.SendToPhoneNumber]: SmartphoneIcon,
};

const EXPIRATION_OPTIONS = [
    { label: '6_hours', value: 6 * SECONDS_IN_HOUR },
    { label: '1_day', value: SECONDS_IN_DAY },
    { label: '1_week', value: 7 * SECONDS_IN_DAY },
    { label: '2_weeks', value: 14 * SECONDS_IN_DAY },
    { label: '1_month', value: 30 * SECONDS_IN_DAY },
];

interface ShareEventModalProps {
    details: EventDetails;
    onClose: CallableFunction;
    users: CustomerUserDetails[];
    setAlert: CallableFunction;
    driver?: ExtendedFleetDriver;
    startAt?: number;
}

const ShareEventModal: React.FC<ShareEventModalProps> = (props: ShareEventModalProps) => {
    const [selectedMethod, setSelectedMethod] = useState<Method>(Method.Link);
    const [recipients, setRecipients] = useState<CustomerUserDetails[]>([]);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [allowSend, setAllowSend] = useState<boolean>(false);
    const [shouldExpire, setShouldExpire] = useState<boolean>(true);
    const [maxAgeSeconds, setMaxAgeSeconds] = useState<number>(SECONDS_IN_DAY);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [useStartAt, setUseStartAt] = useState<boolean>(false);
    const [startAt] = useState<number | undefined>(props.startAt);
    const { api, eventApi } = useApi();
    const { t } = useTranslation();
    const { profile } = useProfile();
    const urlPath = `events/${props.details.event_id}${useStartAt ? `#t=${Math.floor(startAt || 0)}` : ''}`;
    const fullUrl = `${window.location.origin}/${urlPath}`;

    const hasVideo = Object.keys(props.details.artifact_path).some(isEventVideo);

    const relevantMethods = hasVideo
        ? [Method.Link, Method.WhatsApp, Method.SendToUsers, Method.SendToPhoneNumber]
        : [Method.Link, Method.SendToUsers];

    const changeTag = (tagName: string, addTag: boolean) => {
        api.apiV0EventEventIdTagPost({
            eventId: props.details.event_id,
            eventTag: { name: tagName, action: addTag ? EventTagActionEnum.Add : EventTagActionEnum.Remove },
        });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const phoneInput: any = () => {
        const inputElement = (
            <TextField
                fullWidth
                id="phone-number-field"
                size="small"
                label={t('content.coach.send_session.phone_number')}
                inputProps={{ style: { direction: 'ltr' } }}
                sx={{ mb: 1.5 }}
            />
        );
        return inputElement;
    };

    const startAtCheckbox = (
        <FormControlLabel
            control={
                <Checkbox
                    id="start-at-checkbox"
                    data-testid="start-at-checkbox"
                    checked={useStartAt}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setUseStartAt(e.target.checked)}
                    size="small"
                    sx={{ '& .MuiSvgIcon-root': { height: 16, width: 16 } }}
                />
            }
            label={
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {`${t('content.events.share_event.start_at')}${secondsToDurationString(Math.floor(startAt || 0))}`}
                </Typography>
            }
        />
    );

    let content;
    if ([Method.WhatsApp, Method.SendToPhoneNumber].includes(selectedMethod)) {
        const handleSend = () => {
            setIsLoading(true);
            eventApi
                .eventV0EventEventIdSharePost({
                    eventId: props.details.event_id,
                    shareEventRequest: {
                        phone_number: phoneNumber,
                        message,
                        allow_send: allowSend,
                        max_age_seconds: shouldExpire ? maxAgeSeconds : undefined,
                        start_at: useStartAt ? Math.floor(startAt || 0) : undefined,
                        url_only: selectedMethod === Method.WhatsApp,
                    },
                })
                .then((res) => {
                    if (selectedMethod === Method.WhatsApp) {
                        const eventUrl = res.data.url;
                        // remove brackets, dashes, plus signs and leading zeros from phone number
                        const phone = phoneNumber.replace(/[-()\s+]/g, '').replace(/^0+/, '');
                        if (eventUrl) {
                            const url = `https://wa.me/${
                                profile.locale === 'il' ? '972' : '1'
                            }${phone}?text=${encodeURIComponent(`${message}:\n${eventUrl}`)}`;
                            window.open(url, '_blank');
                        }
                    } else {
                        props.setAlert({
                            message: t('content.events.send_event_success'),
                            type: 'success',
                            duration: 6000,
                        });
                    }
                    props.onClose();
                })
                .catch(() => {
                    props.setAlert({
                        message: t('content.events.send_event_error'),
                        type: 'error',
                        duration: 6000,
                    });
                })
                .finally(() => setIsLoading(false));
        };

        content = (
            <>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'flex-start' }}>
                    <InputMask
                        mask={PHONE_NUMBER_MASK_MAP[profile.customer.settings.country]}
                        value={phoneNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
                        maskChar=""
                    >
                        {phoneInput}
                    </InputMask>
                    <TextField
                        id="message-field"
                        label={t('content.events.message')}
                        multiline
                        fullWidth
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        variant="outlined"
                    />
                    {startAtCheckbox}
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="allow-send-checkbox"
                                data-testid="allow-send-checkbox"
                                checked={allowSend}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setAllowSend(e.target.checked)}
                                size="small"
                                sx={{ '& .MuiSvgIcon-root': { height: 16, width: 16 } }}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {t('content.events.share_event.allow_send')}
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="should-expire-checkbox"
                                data-testid="should-expire-checkbox"
                                checked={shouldExpire}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setShouldExpire(e.target.checked)}
                                size="small"
                                sx={{ '& .MuiSvgIcon-root': { height: 16, width: 16 } }}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ mt: 0.5, mr: 1 }}>
                                    {t('content.events.share_event.expires_after')}
                                </Typography>
                                <ThemeProvider theme={theme}>
                                    <Select
                                        id="expire-after-select"
                                        data-testid="expire-after-select"
                                        disabled={!shouldExpire}
                                        size="small"
                                        value={maxAgeSeconds}
                                        onChange={(e) => {
                                            setMaxAgeSeconds(parseInt(e.target.value.toString()));
                                        }}
                                        inputProps={{
                                            'data-testid': 'expire-after-dropdown',
                                        }}
                                        variant="outlined"
                                    >
                                        {EXPIRATION_OPTIONS.map((option) => (
                                            <MenuItem
                                                key={option.label}
                                                value={option.value}
                                                id={`${option.label}-option`}
                                                data-testid={`${option.label}-option`}
                                            >
                                                {t(`content.events.share_event.${option.label}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </ThemeProvider>
                            </Box>
                        }
                    />
                </Box>
                <Box sx={{ mt: 1, width: '100%', display: 'flex' }}>
                    <Button
                        id="btn-cancel"
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => props.onClose()}
                        sx={{ mr: 1 }}
                    >
                        {t('content.events.cancel')}
                    </Button>
                    <Button
                        id="btn-send"
                        variant="contained"
                        color="secondary"
                        disabled={!phoneNumber || isLoading}
                        onClick={() => handleSend()}
                        fullWidth
                    >
                        {t('content.events.send')}
                    </Button>
                </Box>
            </>
        );
    } else if (selectedMethod === Method.SendToUsers) {
        const handleSend = () => {
            if (
                recipients.some((recipient) => !OE_ROLES.includes(recipient.role)) &&
                props.details.tags.includes('alert-muted')
            ) {
                changeTag('alert-muted', false);
            }
            setIsLoading(true);
            api.apiV0EventEventIdSendPost({
                eventId: props.details.event_id,
                sendEventRequest: {
                    recipients: recipients.map((u) => u.user_id),
                    message,
                    start_at: useStartAt ? Math.floor(startAt || 0) : undefined,
                },
            })
                .then(() => {
                    props.setAlert({
                        message: t('content.events.send_event_success'),
                        type: 'success',
                        duration: 6000,
                    });
                    props.onClose();
                })
                .catch(() => {
                    props.setAlert({
                        message: t('content.events.send_event_error'),
                        type: 'error',
                        duration: 6000,
                    });
                })
                .finally(() => setIsLoading(false));
        };

        content = (
            <>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'flex-start' }}>
                    <Autocomplete
                        id="recipients-field"
                        multiple
                        disableCloseOnSelect
                        fullWidth
                        size="small"
                        options={props.users.filter(
                            (u) =>
                                flattenRoles(eventsPageDefs).includes(u.role) &&
                                (!props.driver?.sub_fleet ||
                                    !u.sub_fleets ||
                                    u.sub_fleets.length === 0 ||
                                    u.sub_fleets.includes(props.driver?.sub_fleet)),
                        )}
                        value={recipients}
                        onChange={(_event, newValue) => setRecipients(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth label={t('content.events.recipients')} />
                        )}
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option) => {
                            return (
                                <MenuItem id={`user-${option.user_id}-menu-item`} {...props} key={option.user_id}>
                                    {getUserAvatar(option)}
                                    <Typography variant="body2">{option.name}</Typography>
                                </MenuItem>
                            );
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <TextField
                        id="message-field"
                        label={t('content.events.message')}
                        multiline
                        fullWidth
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        variant="outlined"
                    />
                    {startAtCheckbox}
                </Box>
                <Box sx={{ mt: 1, width: '100%', display: 'flex' }}>
                    <Button
                        id="btn-cancel"
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => props.onClose()}
                        sx={{ mr: 1 }}
                    >
                        {t('content.events.cancel')}
                    </Button>
                    <Button
                        id="btn-send"
                        variant="contained"
                        color="secondary"
                        disabled={recipients.length === 0 || isLoading}
                        onClick={() => handleSend()}
                        fullWidth
                    >
                        {t('content.events.send')}
                    </Button>
                </Box>
            </>
        );
    } else if (selectedMethod === Method.Link) {
        content = (
            <>
                <Divider sx={{ mb: 1.5 }} />
                <OutlinedInput
                    id="link-field"
                    fullWidth
                    value={fullUrl}
                    inputProps={{
                        readOnly: true,
                    }}
                    endAdornment={
                        <Chip
                            label="Copy"
                            onClick={() => {
                                navigator.clipboard.writeText(fullUrl);
                                props.setAlert({
                                    message: t('content.events.share_link_copied'),
                                    type: 'info',
                                    duration: 6000,
                                });
                            }}
                            sx={{ cursor: 'pointer' }}
                        />
                    }
                />
                {startAtCheckbox}
            </>
        );
    }

    return (
        <Dialog
            id="send-event-dialog"
            open={true}
            maxWidth="xs"
            fullWidth
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick' || recipients.length === 0) {
                    props.onClose();
                }
            }}
            sx={{ '& .MuiDialog-scrollPaper': { alignItems: 'start' } }}
        >
            <Box
                sx={{
                    backgroundColor: 'secondary.main',
                    top: 0,
                    p: 1,
                    zIndex: 2,
                    minHeight: 0,
                }}
            >
                <Box sx={{ position: 'absolute', display: 'flex', right: 0, top: 0, mt: 0.5, mr: 0.5 }}>
                    <IconButton id="btn-close-modal" size="small" onClick={() => props.onClose()}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ShareIcon sx={{ mr: 0.5 }} />
                    <Typography>{t('content.events.share_event.header')}</Typography>
                </Box>
            </Box>
            <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-around', my: 1 }}>
                    {relevantMethods.map((method) => {
                        const MethodIcon = METHOD_TO_ICON[method];
                        return (
                            <Box
                                key={method}
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100px' }}
                            >
                                <IconButton
                                    id={`btn-${method}`}
                                    onClick={() => {
                                        setSelectedMethod(method);
                                    }}
                                    sx={{
                                        width: '60px',
                                        height: '60px',
                                        boxShadow:
                                            selectedMethod === method ? 'rgba(0, 0, 0, 0.35) 0px 1px 10px;' : undefined,
                                        transition: 'box-shadow 0.3s',
                                        mb: 1,
                                    }}
                                >
                                    <MethodIcon
                                        sx={{
                                            fontSize: 36,
                                            color: selectedMethod === method ? palette.primary : 'inherit',
                                            transition: 'color 0.3s',
                                        }}
                                    />
                                </IconButton>
                                {[Method.SendToUsers, Method.SendToPhoneNumber].includes(method) ? (
                                    <>
                                        <Typography fontSize={12} textAlign="center">
                                            {t('content.events.share_event.send_to')}
                                        </Typography>
                                        <Typography fontSize={12} textAlign="center">
                                            {t(
                                                `content.events.share_event.${
                                                    method === Method.SendToUsers ? 'users' : 'phone_number'
                                                }`,
                                            )}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography fontSize={12} textAlign="center">
                                        {t(
                                            `content.events.share_event.${
                                                method === Method.Link ? 'link' : 'whatsapp'
                                            }`,
                                        )}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                {content}
            </Box>
        </Dialog>
    );
};

export default ShareEventModal;
